import { useState } from "react";
import { differenceInMilliseconds, format } from "date-fns";
import { DYNAMIC_UTC_OFFSET, useTimer } from "@Utils";
import { parseDateTime } from "@Utils/parseDateTime";

export const useEarlyBirdClock = (timeStamp: number | undefined): string => {
    const getCounterText = () => {
        if (!timeStamp) return "";

        const currentDate = parseDateTime(Date.now());
        const targetDate = parseDateTime(timeStamp - DYNAMIC_UTC_OFFSET);

        const diff = new Date(
            differenceInMilliseconds(targetDate, currentDate)
        );

        // The followings are values for the respective type of date-time entities
        // Date and Month start from 1 instead of 0 so while converting a
        // date or  month diff to a date, that needs to be accounted for
        const month = `${parseInt(format(diff, "M"), 10) - 1}`;
        const date = `${parseInt(format(diff, "dd"), 10) - 1}`;
        const hour = format(diff, "HH");
        const minute = format(diff, "mm");
        const second = format(diff, "ss");

        // The followings are booleans for whether to show that type of date-time entity

        // * [Note added on Sept 28, 2022]: Since the value is being stored as a string instead of a
        // * number after migrating from moment-js to date-fns, the empty check needs to explicitly
        // * check for the string "0", which was previously just a !! check that worked on the number 0
        const showMonth = month !== "0";
        const showDay = showMonth || date !== "0";
        const showHour = showDay || hour !== "0";
        const showMinute = showHour || minute !== "0";
        const showSecond = showMinute || second !== "0";

        if (!(showMonth || showDay || showHour || showMinute || showSecond))
            return "";

        return `
        ${showMonth ? `${month}mo : ` : ""}
        ${showDay ? `${date}d : ` : ""}
        ${showHour ? `${hour}h : ` : ""}
        ${showMinute ? `${minute}m : ` : ""}
        ${showSecond ? `${second}s` : ""}`.replace(/\s+/g, " ");
        // The .replace in the end replaces all whitespace characters
        // (newlines, multiple spaces, etc) with just one space.
    };

    const [timeDiff, setTimeDiff] = useState(getCounterText());

    useTimer(!!timeStamp, () => setTimeDiff(getCounterText()));

    return timeDiff;
};
